/**
 * PATH: packages/email-hub/src/feature/reader-header/triage/ReviewQueue.tsx
 * FILE_ID: EMAILHUB-READERHEADER-TRIAGE-REVIEW-QUEUE-001
 * PURPOSE: List every pending decision in priority order with per-row
 *   accept/reject, stale-version blocking, and advance-on-resolve.
 * ROLE IN ARCHITECTURE: Portalled overlay opened from the "+N reviews" pill.
 * MAIN EXPORTS: ReaderReviewQueue.
 * NON-RESPONSIBILITIES: Transports live in useDecisionTransports; queue
 *   ordering lives in buildDecisionQueue.
 * NOTES FOR FUTURE AI: Rows render from the LIVE queue (resolve → the row
 *   leaves = advanceDecisionQueue semantics). Staleness compares the live
 *   versionToken against the snapshot taken when the panel OPENED — a
 *   mismatch means the state changed since suggested; blind accept is blocked
 *   (plan "Optimism, undo, failure, and concurrency").
 */
import { useEffect, useRef, useState, type RefObject } from 'react'
import { AlertTriangle, Check, X } from 'lucide-react'
import { AdaptiveOverlay } from '@quotech/ui'

import { cn } from '../../../_internal/lib/utils'
import type { ReaderDecision } from '../model/types'
import { decisionHue } from './hues'
import {
  decisionHasConfirmCircle,
  type DecisionVerdict,
} from './useDecisionTransports'

/**
 * A row is stale when the panel-open snapshot recorded a DIFFERENT version
 * token for the same decision id. Pure I → O for direct unit testing.
 */
function reviewRowIsStale(
  live: ReaderDecision,
  snapshotTokens: ReadonlyMap<string, string | null>,
): boolean {
  if (!snapshotTokens.has(live.id)) return false
  return snapshotTokens.get(live.id) !== live.versionToken
}

interface ReviewRowProps {
  decision: ReaderDecision
  stale: boolean
  onResolve: (decision: ReaderDecision, verdict: DecisionVerdict) => void
}

function ReviewRow({ decision, stale, onResolve }: ReviewRowProps) {
  const hue = decisionHue(decision.kind)
  const confirmable = decisionHasConfirmCircle(decision) && !stale
  return (
    <li
      className={cn('rounded-lg border-l-2 bg-muted/20 p-2.5', hue.accent)}
      data-testid="reader-review-row"
      data-decision-id={decision.id}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <span
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', hue.dot)}
              aria-hidden
            />
            <span className="min-w-0 truncate text-[12px] font-semibold">
              {decision.label}
              <span className="font-normal text-muted-foreground">
                {' '}
                · {decision.target}
              </span>
            </span>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {decision.explanation}
          </p>
          <dl className="mt-1.5 grid gap-0.5 text-[10px] text-muted-foreground">
            <div className="flex gap-1">
              <dt className="shrink-0 font-medium text-emerald-700 dark:text-emerald-300">
                Accept:
              </dt>
              <dd>{decision.acceptEffect}</dd>
            </div>
            <div className="flex gap-1">
              <dt className="shrink-0 font-medium text-red-700 dark:text-red-400">
                Reject:
              </dt>
              <dd>{decision.rejectEffect}</dd>
            </div>
            <div className="flex gap-1">
              <dt className="shrink-0 font-medium">Visibility:</dt>
              <dd>{decision.visibilityImpact}</dd>
            </div>
          </dl>
          {stale && (
            <p
              className="mt-1.5 inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300"
              data-testid="reader-review-stale"
            >
              <AlertTriangle className="h-3 w-3" aria-hidden />
              Changed since suggested — review before accepting.
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {confirmable && (
            <button
              type="button"
              onClick={() => onResolve(decision, 'accept')}
              title={`Accept — ${decision.acceptEffect}`}
              aria-label={`Accept: ${decision.label}`}
              data-testid="reader-review-accept"
              className={cn(
                'relative inline-flex h-6 w-6 items-center justify-center rounded-full border shadow-sm transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1',
                // change #269 C1: rows here are auto-height (no fixed band
                // to overflow), so this secondary confirm target gets the
                // SAME real 44px growth as the primary decision circles
                // (DecisionCircles.tsx) instead of the WORK ITEM 7
                // pseudo-hit-area. Flex `gap-1` keeps it clear of Reject —
                // real boxes push each other apart; no overlap risk like a
                // negative-inset pseudo-element had.
                'pointer-coarse:h-11 pointer-coarse:w-11',
                hue.fill,
              )}
            >
              <Check className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
          <button
            type="button"
            onClick={() => onResolve(decision, 'reject')}
            title={`Reject — ${decision.rejectEffect}`}
            aria-label={`Reject: ${decision.label}`}
            data-testid="reader-review-reject"
            className={cn(
              'relative inline-flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1',
              // change #269 C1: same real 44px growth as Accept above.
              'pointer-coarse:h-11 pointer-coarse:w-11',
            )}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </div>
    </li>
  )
}

export interface ReaderReviewQueueProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** LIVE priority-ordered queue; resolved rows have already advanced out. */
  decisions: readonly ReaderDecision[]
  onResolve: (decision: ReaderDecision, verdict: DecisionVerdict) => void
  /** Compact density renders the bottom sheet instead of the popover. */
  compact: boolean
  anchorRef: RefObject<HTMLElement | null>
  /** Called after the queue empties and the panel closes (focus the summary). */
  onExhausted?: () => void
}

export function ReaderReviewQueue({
  open,
  onOpenChange,
  decisions,
  onResolve,
  compact,
  anchorRef,
  onExhausted,
}: ReaderReviewQueueProps) {
  // Version-token snapshot at open time; staleness is measured against it.
  const [snapshot, setSnapshot] = useState<Map<string, string | null>>(
    () => new Map(),
  )
  const wasOpen = useRef(false)
  useEffect(() => {
    if (open && !wasOpen.current) {
      setSnapshot(
        new Map(decisions.map((decision) => [decision.id, decision.versionToken])),
      )
    }
    wasOpen.current = open
    // Snapshot only on the closed→open transition, never on queue changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Zero remaining decisions: close and hand focus back to the summary.
  useEffect(() => {
    if (open && decisions.length === 0) {
      onOpenChange(false)
      onExhausted?.()
    }
  }, [decisions.length, onExhausted, onOpenChange, open])

  return (
    <AdaptiveOverlay
      open={open}
      onOpenChange={onOpenChange}
      anchorRef={anchorRef}
      mode={compact ? 'sheet' : 'popover'}
      label="Review queue"
      testId="reader-review-queue"
      popoverWidth={384}
    >
      <div className="flex items-center justify-between px-1 pb-2 pt-1">
        <span className="text-sm font-semibold">
          Reviews ({decisions.length})
        </span>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent pointer-coarse:h-11 pointer-coarse:w-11"
          aria-label="Close review queue"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <ol className="grid gap-2">
        {decisions.map((decision) => (
          <ReviewRow
            key={decision.id}
            decision={decision}
            stale={reviewRowIsStale(decision, snapshot)}
            onResolve={onResolve}
          />
        ))}
      </ol>
    </AdaptiveOverlay>
  )
}
